import { SentSuggestionSchema } from '@/schemas/api/ai/suggest-price';
import { blankPriceAsAbsent } from '@/schemas/api/listings/create';
import { z } from 'zod';
import { ErrorResponseSchema } from '@/schemas/common/base';
import {
  idValidator,
  priceValidatorCoerce,
  latitudeValidatorCoerceNullable,
  longitudeValidatorCoerceNullable,
  idValidatorNonNegativeOptional,
  idValidatorOptional,
} from '@/schemas/common/validators';

/**
 * Request schema for editing a listing
 * All fields are required except for optional ones like text, vin, mileage, etc.
 */
export const EditListingRequestSchema = z.object({
  price: priceValidatorCoerce.max(999999, 'price must be less than 1,000,000'),
  title: z.string().min(3, 'Title must be at least 3 characters').max(255, 'Title must be at most 255 characters'),
  text: z.string().optional(),
  vin: z.union([z.string(), z.number()]).optional(),
  mileage: z.union([z.string(), z.number()]).optional(),
  /**
   * Null is "not given" and keeps the stored point. z.coerce.number() alone
   * read a JSON null as 0; the route's refusal of a 0 pair is what kept such an
   * edit off 0,0 (the address schema had no such guard, see addresses/edit.ts).
   */
  latitude: latitudeValidatorCoerceNullable.optional(),
  longitude: longitudeValidatorCoerceNullable.optional(),
  /** Ignored for a seller's own listing: the town is derived (utils/listing-location-text.ts). */
  locationText: z.string().nullable().optional(),
  /**
   * Which of the seller's saved addresses it ships from. Null puts it back on
   * their default; absent keeps what it has.
   */
  addressId: z.coerce.number().int().positive().nullable().optional(),
  itemCondition: idValidatorNonNegativeOptional,
  shippable: z.union([z.boolean(), z.coerce.number()]).optional(),
  categoryId: idValidator,
  categoryGroupId: idValidatorOptional,
  height: z.union([z.number(), z.string()]).optional(),
  width: z.union([z.number(), z.string()]).optional(),
  weight: z.union([z.number(), z.string()]).optional(),
  depth: z.union([z.number(), z.string()]).optional(),
  /** Shippo USPS parcel template token the seller chose; empty or unknown means custom dimensions */
  parcelTemplate: z.string().max(40).nullable().optional(),
  photo: z.union([
    z.number(),
    z.object({
      totalCount: z.number(),
      actions: z.array(z.any()).optional(),
      /** Map of new positions: each entry is an old photo index (number) or 'new' for uploads */
      sources: z.array(z.union([z.number(), z.literal('new')])).optional(),
    }),
  ]).optional(),
  photosChanged: z.boolean().optional(),
  /** Floor price - minimum price seller will accept. Blank keeps the stored one. */
  floorPrice: blankPriceAsAbsent,
  /** System-recommended price. Blank keeps the stored one. */
  recommendedPrice: blankPriceAsAbsent,
  /** The edit page's AI price suggestion, kept when the listing is saved at it (utils/price-suggestion-signal.ts). */
  priceSuggestion: SentSuggestionSchema.optional(),
});

/**
 * Schema for presigned URL response item
 */
export const PresignedUrlItemSchema = z.object({
  photoIndex: z.number(),
  size: z.string(),
  presignedUrl: z.string(),
});

/**
 * Schema for edited listing data returned in response
 */
export const EditedListingDataSchema = z.object({
  id: z.number(),
  title: z.string(),
  price: z.string(),
});

/**
 * Success response schema for edit listing endpoint
 */
export const EditListingSuccessSchema = z.object({
  success: z.literal(true),
  listing: EditedListingDataSchema,
  presignedUrls: z.array(PresignedUrlItemSchema).optional(),
  /**
   * True when the listing is held for an admin to review before it goes live
   * (a phone number, a WhatsApp/Telegram contact or a supplier pitch in its
   * text; utils/listing-spam.ts). The seller is told it is under review.
   */
  underReview: z.boolean().optional(),
});

/**
 * Response schema for PUT/POST /api/listings/[id]/edit
 * Returns updated listing info with optional presigned URLs for photo uploads
 */
export const EditListingResponseSchema = z.discriminatedUnion('success', [
  EditListingSuccessSchema,
  ErrorResponseSchema,
]);

export type EditListingRequest = z.infer<typeof EditListingRequestSchema>;
export type EditListingResponse = z.infer<typeof EditListingResponseSchema>;
export type PresignedUrlItem = z.infer<typeof PresignedUrlItemSchema>;
export type EditedListingData = z.infer<typeof EditedListingDataSchema>;
